"use client"

import { useRemoteClientPost } from "@/configs/http-client"
import { Endpoints } from "@/remote/endpoints"
import { useLocalStorage } from "@/remote/local-storage"
import { DataTable } from "@/view/components/data-table-member-gym-with-options"
import { PaginationDemo } from "@/view/components/pagination"
import { usePagination, useToast } from "@/view/hooks"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Input } from "../../../components/ui/input"
import { Button } from "../../../components/ui/button"
import { Badge } from "../../../components/ui/badge"
import { X, Filter } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu"

export function DisplayMemberGym() {
  const { httpClientPost } = useRemoteClientPost()
  const { backPage, nextPage } = usePagination()
  const { getUnitId } = useLocalStorage()
  const [params] = useSearchParams()
  const [data, setData] = useState<any[]>([])
  const [searchName, setSearchName] = useState("")
  const [loading, setLoading] = useState(true)
  const { showToast } = useToast()
  const [controlPage, setControlPage] = useState(0)

  // Status filter state
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([1, 2, 3, 4, 5])

  const statusOptions = {
    "1": { label: "Ativo", color: "bg-green-200 border-green-300 text-green-800" },
    "2": { label: "Pendente", color: "bg-yellow-200 border-yellow-300 text-yellow-800" },
    "3": { label: "Bloqueado", color: "bg-gray-200 border-gray-300 text-gray-800" },
    "4": { label: "Inadimplente", color: "bg-orange-200 border-orange-300 text-orange-800" },
    "5": { label: "Cancelado", color: "bg-purple-200 border-purple-300 text-purple-800" },
    // "0": { label: "Deletado", color: "bg-red-200 border-red-300 text-red-800" },
  }

  const [pageInfo, setPageInfo] = useState({
    page: 0,
    total: 0,
    totalPages: 0,
  })
  const quantityForPage = 10

  const getNextPage = () => {
    nextPage(pageInfo.page, pageInfo.totalPages, setControlPage)
    setPageInfo((prev) => ({ ...prev, page: controlPage }))
  }

  const getBackPage = () => {
    backPage(pageInfo.page, setControlPage)
    setPageInfo((prev) => ({ ...prev, page: controlPage }))
  }

  const fetchData = async () => {
    try {
      const response = await httpClientPost(
        `${Endpoints.students}/filtro?page=${controlPage}&size=${quantityForPage}&order=desc&sort=id`,
        {
          unidadeId: params.get("id") ?? getUnitId(),
          status: selectedStatuses.length > 0 ? selectedStatuses : [1, 2, 3, 4, 5], // Default to all non-deleted if none selected
          nome: searchName.length >= 3 ? searchName : "",
        },
      )

      const data = response?.data?.data ?? {}
      const content = data?.content ?? []
      const page = data?.pageable?.pageNumber ?? 0
      const totalPages = data?.totalPages
      const total = data?.totalElements

      setData(content)
      setPageInfo({
        page: page,
        total: total,
        totalPages: totalPages,
      })
      if (loading) {
        showToast("Dados carregados com sucesso", "success")
      }
      setLoading(false)
    } catch (error) {
      console.error("Erro ao buscar dados:", error)
      showToast("Erro ao carregar dados", "error")
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    fetchData()
  }

  const clearSearch = () => {
    setSearchName("")
    setControlPage(0)
  }

  const toggleStatus = (status: number) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status)
      } else {
        return [...prev, status]
      }
    })
    setControlPage(0) // Reset to first page when changing filters
  }

  const selectAllStatuses = () => {
    setSelectedStatuses([0, 1, 2, 3, 4, 5])
    setControlPage(0)
  }

  const clearAllStatuses = () => {
    setSelectedStatuses([])
    setControlPage(0)
  }

  // Get active filter count for badge
  const activeFilterCount = 5 - selectedStatuses.length

  useEffect(() => {
    if (searchName.length === 0 || searchName.length >= 3) {
      handleRefresh()
    }
  }, [controlPage, params, searchName, selectedStatuses])

  return (
    <>
      <div className="mb-4 flex flex-col sm:flex-row gap-2">
        <div className="flex items-center gap-2 max-w-sm">
          <Input
            type="text"
            placeholder="Pesquisar aluno por nome (mín. 3 letras)..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="flex-1 w-[300px] sm:w-[400px] md:w-[500px] lg:w-[600px] xl:w-[700px] 2xl:w-[800px] h-10"
          />
          {searchName && (
            <Button variant="outline" size="icon" onClick={clearSearch} className="shrink-0">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex gap-2">
                <Filter className="h-4 w-4" />
                Filtrar por Status
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              <DropdownMenuLabel>Filtrar por Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {Object.entries(statusOptions).map(([statusId, { label, color }]) => (
                <DropdownMenuCheckboxItem
                  key={statusId}
                  checked={selectedStatuses.includes(Number.parseInt(statusId))}
                  onCheckedChange={() => toggleStatus(Number.parseInt(statusId))}
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={color}>
                      {label}
                    </Badge>
                  </div>
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <div className="flex justify-between px-2 py-1.5">
                <Button variant="ghost" size="sm" onClick={selectAllStatuses}>
                  Selecionar Todos
                </Button>
                <Button variant="ghost" size="sm" onClick={clearAllStatuses}>
                  Limpar
                </Button>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Status filter badges */}
          <div className="flex flex-wrap gap-1 items-center">
            {selectedStatuses.length > 0 && selectedStatuses.length < 6 && (
              <div className="flex flex-wrap gap-1">
                {selectedStatuses.map((statusId) => (
                  <Badge
                    key={statusId}
                    variant="outline"
                    className={`${statusOptions[String(statusId) as keyof typeof statusOptions].color} cursor-pointer`}
                    onClick={() => toggleStatus(statusId)}
                  >
                    {statusOptions[String(statusId) as keyof typeof statusOptions].label}
                    <X className="ml-1 h-3 w-3" />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <>
          <DataTable data={data} getRefreshList={handleRefresh} />
          <PaginationDemo
            totalForPage={quantityForPage}
            back={getBackPage}
            next={getNextPage}
            page={pageInfo.page}
            total={pageInfo.total}
            labelItem="alunos"
          />
        </>
      )}
    </>
  )
}
